import React from 'react';
import { CrmConfig, CrmLog, ActiveAgent, parseDate, formatNameUrl, formatPhoneUrl, formatDisplayHtml } from './crmUtils';

interface CrmCallLogsTabProps {
  activeTab: 'logs' | 'chat' | 'cdr' | 'voicemails';
  isLight: boolean;
  crm?: CrmConfig;
  logs: CrmLog[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  maxRows: number;
  hotCallWindow: number;
  activeAgents: ActiveAgent[];
  now: number;
  timeSkew: number;
  scrollRef: React.RefObject<HTMLTableSectionElement | null>;
  setShowAgentModal: (show: boolean) => void;
  setCallLogsSettingsOpen: (show: boolean) => void;
  setChatTrigger: (trigger: { contact: string; did?: string; ts: number; autoLookup?: boolean }) => void;
  defaultChatDid: string;
  setActiveTab: (tab: 'logs' | 'chat' | 'cdr' | 'voicemails') => void;
  setShowHeaderMobile: (show: boolean) => void;
  clientTimezone: string;
  onEditContact: (phone: string, name: string) => void;
}

export function CrmCallLogsTab({
  activeTab,
  isLight,
  crm,
  logs,
  page,
  setPage,
  maxRows,
  hotCallWindow,
  activeAgents,
  now,
  timeSkew,
  scrollRef,
  setShowAgentModal,
  setCallLogsSettingsOpen,
  setChatTrigger,
  defaultChatDid,
  setActiveTab,
  setShowHeaderMobile,
  clientTimezone,
  onEditContact
}: CrmCallLogsTabProps) {
  if (crm?.enableCurrentCallers === false) return null;

  const displayedLogs = logs.slice(page * maxRows, (page + 1) * maxRows);

  let activeCount = 0;
  let inactiveCount = 0;
  const adjustedNow = now - timeSkew;
  activeAgents.forEach(agent => {
    const isIdle = agent.status ? agent.status === 'inactive' : (adjustedNow - agent.lastActive) > 60000;
    if (isIdle) inactiveCount++;
    else activeCount++;
  });

  return (
    <div
      className="call-log-layout"
      style={{
        display: activeTab === 'logs' ? 'flex' : 'none',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: '1rem',
        width: '100%'
      }}
    >
      <div
        className="glass-panel current-callers-panel"
        style={{
          width: '100%',
          maxWidth: '500px',
          display: 'flex',
          flexDirection: 'column',
          background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
          border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.05)',
          borderRadius: '12px',
          padding: '0',
          overflow: 'hidden'
        }}
      >
        <div style={{ overflowY: 'visible', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead
              style={{
                background: isLight ? 'rgba(255,255,255,0.9)' : 'rgba(24, 24, 27, 0.9)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}
            >
              <tr>
                <th
                  style={{
                    padding: '0.75rem 1rem',
                    color: isLight ? '#4b5563' : '#a1a1aa',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>Current Callers</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal' }}>
                    <div
                      onClick={(e) => { e.stopPropagation(); setShowAgentModal(true); }}
                      title="View Agent Status"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        background: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
                        padding: '0.2rem 0.6rem',
                        borderRadius: '16px',
                        fontSize: '0.75rem',
                        color: isLight ? '#4b5563' : '#a1a1aa',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e' }}></span>
                        <span>Active {activeCount}</span>
                      </div>
                      <div style={{ width: '1px', height: '10px', background: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }}></div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span>{inactiveCount} Idle</span>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fbbf24' }}></span>
                      </div>
                    </div>

                    <div style={{ width: '1px', height: '12px', background: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }}></div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCallLogsSettingsOpen(true); }}
                      title="Call Logs Settings"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        color: isLight ? '#0ea5e9' : '#38bdf8',
                        opacity: 0.8,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody ref={scrollRef}>
              {displayedLogs.length === 0 ? (
                <tr><td style={{ padding: '3rem', textAlign: 'center', color: '#71717a' }}>Waiting for calls...</td></tr>
              ) : (
                displayedLogs.map((log: any, idx: number) => {
                  const isHit = log.resolvedStatus === 'HIT' || log.resolvedStatus === 'MISS' || log.resolvedStatus === 'BYPASS';
                  let rawCallerName = log.responsePayload || '-';

                  if (isHit && log.responsePayload) {
                    try {
                      const body = JSON.parse(log.responsePayload);
                      if (body.name) rawCallerName = body.name;
                    } catch {}
                  }

                  const urlName = formatNameUrl(rawCallerName, crm?.nameUrlFormat || '');
                  const urlPhone = formatPhoneUrl(log.targetNumber, crm?.phoneUrlFormat || '');
                  const parsedTime = parseDate(log.createdAt, clientTimezone);

                  return (
                    <tr
                      key={log.id}
                      style={{
                        borderBottom: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.05)',
                        background: idx === 0 ? (isLight ? 'rgba(56, 189, 248, 0.1)' : 'rgba(56, 189, 248, 0.03)') : 'transparent',
                        transition: 'background 0.5s'
                      }}
                    >
                      <td style={{ padding: '0.5rem 1rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {formatDisplayHtml(rawCallerName, log.targetNumber, crm, urlName, urlPhone, log.createdAt, isLight, hotCallWindow, false, false, false, onEditContact)}
                            {crm?.enableChatClient && (
                              <button
                                onClick={() => {
                                  setChatTrigger({ contact: log.targetNumber, did: defaultChatDid, ts: Date.now(), autoLookup: true });
                                  setActiveTab('chat');
                                  setShowHeaderMobile(false);
                                }}
                                title="Send Text Message"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: isLight ? '#0ea5e9' : '#38bdf8',
                                  padding: '0.2rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  opacity: 0.8,
                                  transition: 'opacity 0.2s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                              </button>
                            )}
                          </div>
                          <span style={{ fontSize: '0.7rem', color: isLight ? '#6b7280' : '#71717a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Time: <strong style={{ color: isLight ? '#4b5563' : '#a1a1aa' }}>{parsedTime.timeStr}</strong>
                            {parsedTime.dateStr && (
                              <> - Date: <strong style={{ color: isLight ? '#4b5563' : '#a1a1aa' }}>{parsedTime.dateStr}</strong></>
                            )}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Unified Pagination Nav */}
        {(page > 0 || (page + 1) * maxRows < logs.length) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '1rem',
              borderTop: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.05)',
              background: isLight ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div
                onClick={() => setPage(Math.max(0, Math.ceil(logs.length / maxRows) - 1))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: (page + 1) * maxRows < logs.length ? 'pointer' : 'default',
                  opacity: (page + 1) * maxRows < logs.length ? 1 : 0.3,
                  color: isLight ? '#09090b' : 'white',
                  transition: 'opacity 0.2s'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 17 6 12 11 7"></polyline><polyline points="18 17 13 12 18 7"></polyline></svg>
              </div>
              <div
                onClick={() => setPage(p => (p + 1) * maxRows < logs.length ? p + 1 : p)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: (page + 1) * maxRows < logs.length ? 'pointer' : 'default',
                  opacity: (page + 1) * maxRows < logs.length ? 1 : 0.3,
                  color: isLight ? '#09090b' : 'white',
                  fontWeight: 600,
                  transition: 'opacity 0.2s'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg> Older
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div
                onClick={() => setPage(p => Math.max(0, p - 1))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: page > 0 ? 'pointer' : 'default',
                  opacity: page > 0 ? 1 : 0.3,
                  color: isLight ? '#09090b' : 'white',
                  fontWeight: 600,
                  transition: 'opacity 0.2s'
                }}
              >
                Newer <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </div>
              <div
                onClick={() => setPage(0)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: page > 0 ? 'pointer' : 'default',
                  opacity: page > 0 ? 1 : 0.3,
                  color: isLight ? '#09090b' : 'white',
                  transition: 'opacity 0.2s'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="13 17 18 12 13 7"></polyline><polyline points="6 17 11 12 6 7"></polyline></svg>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
